/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { Button, Typography, breakpoints } from '@coursera/cds-core';

import { useCoursePlanningDialog } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/context/CoursePlanningDialogContext';
import { PATHS } from 'bundles/degree-home/components/v3-dpm/constants/constants';
import { PLAN_FOR_GRADUATION_BUTTON } from 'bundles/degree-home/components/v3-dpm/constants/trackingNameConstants';
import useCreatePlan from 'bundles/degree-home/components/v3-dpm/lib/useCreatePlanMutation';
import useDegreePlan from 'bundles/degree-home/components/v3-dpm/lib/useDegreePlan';
import TrackedButton from 'bundles/page/components/TrackedButton';
import constants from 'bundles/premium-certificate/constants/constants';
import { Card } from 'bundles/program-loggedin-shared/components/Card';

import planGraduationBackgroundImage from 'images/degree-home/plan-graduation-prompt-background-2x.png';

import _t from 'i18n!nls/degree-home';

const styles = {
  cardContainer: css`
    display: flex;
    flex-direction: row;
    ${breakpoints.up('sm')} {
      background-image: url(${planGraduationBackgroundImage});
      background-repeat: no-repeat;
      background-size: contain;
      background-position: 100%;
    }
    ${breakpoints.only('md')} {
      /* This forces the background image to not collide with the left text */
      background-position: max(300px, 100%) 50%;
    }
  `,
  innerContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-300);
    flex-basis: 60%;

    ${breakpoints.only('xs')} {
      flex-basis: 100%;
    }
    ${breakpoints.only('md')} {
      flex-basis: 328px;
    }
  `,
};

type Props = {
  variant: string;
};

export const PlanGraduationPrompt = ({ variant }: Props) => {
  const { createPlan, createPlanLoading } = useCreatePlan();
  const { openCourseDialog } = useCoursePlanningDialog();
  const { degreeSlug } = useParams();
  const { data, loading: degreePlanLoading } = useDegreePlan();

  if (data?.LearnerPlan?.queryPlan || degreePlanLoading) {
    return null;
  }

  const handleClick = async () => {
    const createPlanSuccess = await createPlan({ openCourseDialog });

    if (variant === 'in-progress' && createPlanSuccess) {
      // Navigating using router doesn't appear to work with the tabs interface
      window.location.pathname = `/${constants.degreeUrlRoot}/${degreeSlug}/${PATHS.ACADEMICS}`;
    }
  };

  return (
    <Card css={styles.cardContainer}>
      <div css={styles.innerContainer}>
        <Typography variant="h2">{_t('Plan for graduation and find out when you can earn your degree')}</Typography>

        <Button
          loading={createPlanLoading}
          trackingName={PLAN_FOR_GRADUATION_BUTTON}
          component={TrackedButton}
          withVisibilityTracking
          requireFullyVisible={true}
          onClick={handleClick}
          size="small"
          aria-label={_t('Plan for graduation')}
        >
          {_t('Plan for graduation')}
        </Button>
      </div>
    </Card>
  );
};

export default PlanGraduationPrompt;
